<script setup lang="ts">
/** Corporate Secretarial Service Agreement — SG Incorporation (Post) */
import { onMounted } from 'vue';
import CddVarField from '../incorporation-pre/cdd/CddVarField.vue';
import { useCddFill } from '../incorporation-pre/cdd/useCddFill';
import SgPostSigners from './SgPostSigners.vue';
import { SG_POST_SIGNER_KEYS } from './sgPostSignersContext';
import './allotDoc.css';

const fill = useCddFill();

/** Word: [[if service_*]]☑[[else]]☐[[endif]] */
const SERVICE_FLAGS = [
  { key: 'service_standard_secretarial', label: 'Standard Annual Secretarial Services', def: 'yes' },
  { key: 'service_registered_office', label: 'Provision of Registered Office', def: 'yes' },
  { key: 'service_nominee_director', label: 'Provision of Nominee Director', def: 'no' },
  { key: 'service_nominee_shareholder', label: 'Provision of Nominee Shareholder', def: 'no' },
] as const;

function isYes(key: string): boolean {
  const v = fill.values[key];
  if (v === true) return true;
  const s = String(v ?? '').trim().toLowerCase();
  return s === 'yes' || s === 'true' || s === '1' || s === 'y' || s === 'checked';
}

function mark(on: boolean) {
  return on ? '☑' : '☐';
}

function toggleService(key: string) {
  fill.setValue(key, isYes(key) ? 'no' : 'yes');
}

onMounted(() => {
  for (const f of SERVICE_FLAGS) {
    if (!String(fill.values[f.key] ?? '').trim()) fill.setValue(f.key, f.def);
  }
});
</script>

<template>
  <div class="allot-doc">
    <div class="doc-head">
      <div class="doc-lbl-title" style="text-align:center; font-size:15px">CORPORATE SECRETARIAL SERVICE AGREEMENT</div>
    </div>

    <p class="doc-p">
      This AGREEMENT is made this <CddVarField field-key="agreement_date" label="Agreement Date" />.
    </p>
    <p class="doc-p"><b>BETWEEN:</b></p>
    <p class="doc-p">
      (1) <CddVarField field-key="company_name" label="Company Name" />
      (Company Registration No. <CddVarField field-key="company_registration_no" label="Registration No." />),
      having its registered office at <CddVarField field-key="company_registered_address" label="Company Registered Address" />,
      a company incorporated in Singapore ("the Company"); and
    </p>
    <p class="doc-p">
      (2) <CddVarField field-key="csp_name" label="CSP Name" />
      (Company Registration No. <CddVarField field-key="csp_uen" label="CSP UEN" />),
      having its registered office at <CddVarField field-key="csp_full_address" label="CSP Full Address" />,
      and its affiliates ("Service Provider"), being a duly registered and authorised corporate secretarial
      services provider.
    </p>

    <p class="doc-p"><b>WHEREAS:</b></p>
    <p class="doc-p">
      The Company, with authority of the Company's Constitution, has requested that the Service Provider
      provide the services of one or more individual(s) to act as a Company Secretary of the Company.
    </p>
    <p class="doc-p"><b>IT IS AGREED AS FOLLOWS:</b></p>

    <p class="doc-sec-title" style="text-align:left">1. NATURE OF SERVICES PROVIDED</p>
    <p class="doc-p">
      The Company, with authority of the Company's Constitution, has requested that the Service Provider
      provide the services of an individual who is hereby appointed to act in the capacity of a resident
      Company Secretary (hereinafter referred to as "Company Secretary") of the Company upon the terms and
      conditions hereto ("Services"). The consideration for such services provided by the Service Provider
      is outlined in the Fees Structure as detailed below in Clause 2 and Clause 3 of this Agreement.
    </p>

    <p class="doc-sec-title" style="text-align:left">2. SCOPE OF SERVICES</p>
    <p class="doc-p">2.1 The scope of Corporate Secretarial Services shall include the following:</p>
    <ul class="sg-bullets">
      <li>(a) Acting as named secretary in ACRA records;</li>
      <li>
        (b) Maintaining and safekeeping of the Minutes and Register Books, namely:
        Register of Members; Register of Directors, Secretaries, Auditors and Managers;
        Register of Directors' Shareholdings; Register of Substantial Shareholdings; Register of Charges;
        Register of Applications and Allotments; Register of Transfers; Register of Debenture Holders, if any;
        Minute books of General and Directors' Meetings;
      </li>
      <li>(c) Proper maintenance of the Company's statutory records;</li>
      <li>(d) Collating documents required for opening a Singapore bank account for the Company;</li>
      <li>(e) Ad hoc advice on standard secretarial / statutory / compliance matters;</li>
      <li>(f) Drafting of standard directors' resolutions and updating the registrar on matters such as change of principal activities and change of financial year end;</li>
      <li>(g) Sending email alerts to the Company in respect of corporate secretarial obligations under the Companies Act; and</li>
      <li>(h) Safekeeping of the Company's Common Seal (if any).</li>
    </ul>
    <p class="doc-p">
      2.2 All correspondence and email alerts will be sent to the Company's email address(es) as designated.
      The Company shall immediately notify the Service Provider of any changes thereto.
    </p>
    <p class="doc-p">
      2.3 The Service Provider reserves the right to refuse, at any time, to proceed with any instructions
      received from the Company, including but not limited to ACRA lodgements, if the Service Provider has
      reasonable grounds to believe that the instructions received are not in accordance with the applicable
      laws of Singapore.
    </p>

    <p class="doc-sec-title" style="text-align:left">3. FEES</p>
    <p class="doc-p">
      3.1 The Annual Fee for the nominee Secretary and provision of Corporate Secretarial Services is
      <CddVarField field-key="currency_symbol" label="Currency" />
      <CddVarField field-key="fee_nominee_secretary" label="Nominee Secretary Fee" />
      and
      <CddVarField field-key="currency_symbol" label="Currency" />
      <CddVarField field-key="fee_corporate_secretarial" label="Corporate Secretarial Fee" />
      (excluding disbursements) respectively, payable at the time of appointment for the first 12-month
      period. Thereafter, the Annual Fee will be billed annually in advance for each subsequent 12-month
      period. The services must be used within one year and are non-refundable in the event of termination.
    </p>
    <p class="doc-p">
      3.2 Any secretarial work in addition to the above Secretarial Services will be charged for separately.
      Additional secretarial work shall include but is not limited to: providing ad hoc advice on complex
      secretarial / statutory / compliance matters, drafting non-standard directors' resolutions, and
      attending general meetings.
    </p>
    <p class="doc-p">
      3.3 All fees are exclusive of disbursements and out-of-pocket expenses incurred in connection with the
      performance of the Services, including but not limited to filing and application fees.
    </p>
    <p class="doc-p">
      3.4 The Service Provider reserves the right not to proceed with any services until the invoices for
      such services and/or other outstanding invoices are fully settled.
    </p>
    <p class="doc-p">
      3.5 The Service Provider reserves the right to amend its Annual Fees subject to one (1) month's prior
      written notice to the Company.
    </p>

    <p class="doc-sec-title" style="text-align:left">4. TENURE AND TERMINATION</p>
    <p class="doc-p">
      4.1 This Agreement will be valid for a period of
      <CddVarField field-key="tenure_years" label="Tenure (Years)" />
      year(s) effective from the date of the appointment of the Service Provider as Company Secretary, and
      is deemed to continue thereafter unless a written notice is given two (2) months in advance to
      discontinue the services.
    </p>
    <p class="doc-p">4.2 Either Party may terminate this Agreement upon two (2) months' prior written notice thereof.</p>
    <p class="doc-p">
      4.3 The Service Provider may terminate this Agreement with immediate effect in the event of suspected
      fraudulent or illegal activities, conviction of a non-compoundable criminal offence carrying
      imprisonment, or material breach of this Agreement by the Company.
    </p>
    <p class="doc-p">
      4.4 In the event that this Agreement is prematurely terminated by the Company prior to completion of
      the minimum period of <CddVarField field-key="tenure_years" label="Tenure (Years)" /> year(s) from the
      date of signature hereof, the Annual Fee for the remaining year(s) will nevertheless be due for payment
      upon termination.
    </p>
    <p class="doc-p">
      4.5 The Service Provider shall only release statutory records, minute books, and other documents once
      all outstanding charges and invoices due to the Service Provider are settled.
    </p>

    <p class="doc-sec-title" style="text-align:left">5. MISCELLANEOUS</p>
    <p class="doc-p">
      5.1 This Agreement together with its Annexes represents the entire agreement between both parties.
      5.2 Severability. 5.3 Assignment by Service Provider; Company assignment requires prior written consent.
      5.4 No waiver. 5.5 Contracts (Rights of Third Parties) Act 2001 — no third-party enforcement rights
      except as expressly provided.
    </p>

    <p class="doc-sec-title" style="text-align:left">6. GOVERNING LAW &amp; JURISDICTION</p>
    <p class="doc-p">
      6.1 This Agreement together with its annexures shall be governed by and construed in accordance with
      the laws of the Republic of Singapore, and the parties agree to submit to the jurisdiction of the
      Courts in Singapore.
    </p>

    <p class="doc-lbl-title">For and on behalf of <CddVarField field-key="csp_name" label="CSP Name" /></p>
    <p class="doc-p">
      Signature: _____________________________<br />
      Name: <CddVarField field-key="csp_signatory_name" label="CSP Signatory" /><br />
      Designation: <CddVarField field-key="csp_signatory_title" label="CSP Title" /><br />
      Date: <CddVarField field-key="agreement_date" label="Agreement Date" />
    </p>

    <p class="doc-p" style="margin-top:16px">
      We, <CddVarField field-key="company_name" label="Company Name" />, confirm our agreement to the terms
      of engagement set out in this Agreement and select your standard service(s) as follows:
    </p>
    <table class="cdd-tbl">
      <thead><tr><th>Service</th><th>Select</th></tr></thead>
      <tbody>
        <tr v-for="f in SERVICE_FLAGS" :key="f.key">
          <td>{{ f.label }}</td>
          <td>
            <span class="doc-toggle" @click="toggleService(f.key)">{{ mark(isYes(f.key)) }}</span>
          </td>
        </tr>
      </tbody>
    </table>
    <p class="doc-p" style="font-size:11px; color:#555">
      (Please indicate the services that you would like us to provide by marking "X" in the appropriate boxes.)
    </p>
    <p class="doc-p">
      We authorise <CddVarField field-key="csp_name" label="CSP Name" /> to make the relevant ACRA BizFile+
      lodgements on our behalf. This covers the necessary lodgements with ACRA for the termination of
      corporate secretarial services.
    </p>

    <!-- Word: company_signatory_name / title / company_signature_date -->
    <p class="doc-lbl-title">For and on behalf of <CddVarField field-key="company_name" label="Company Name" /></p>
    <p class="doc-p">
      Signature: _____________________________<br />
      Name: <CddVarField field-key="company_signatory_name" label="Company Signatory" /><br />
      Designation: <CddVarField field-key="company_signatory_title" label="Company Title" /><br />
      Date: <CddVarField field-key="company_signature_date" label="Company Signature Date" />
    </p>
    <SgPostSigners :var-key="SG_POST_SIGNER_KEYS.cosecAgr" board-label="COMPANY & CSP SIGNERS" />
  </div>
</template>

<style scoped>
.sg-bullets { margin: 0 0 10px; padding-left: 22px; font-size: 12.5px; line-height: 1.55; }
.sg-bullets li { margin-bottom: 4px; }
</style>
